import React from 'react';
import PropTypes from 'prop-types';
import {CardElement} from 'react-stripe-elements';
import { billingConfigs, customers, users } from "../../../ducks";
import {duckify, singleDuck} from "../../../utils/duckify";
import StripeForm from '../StripeForm';
import FA from '../../../utils/FA';
import NachaMessage from '../NachaMessage';
import ConvenienceFeeCheckbox from './ConvenienceFeeCheckbox';
import rollbar from 'utils/rollbarLogger';
import {paymentTypes, paymentMethodDisplayName, paymentInputDefaultProps, paymentInputPropTypes, submitButtonText} from './PaymentSelector';
import PaymentProfileOptions from '../detailInputs/PaymentProfileOptions';
import PaymentDetailsInput from "../detailInputs/PaymentDetailsInput";
import {faExclamationTriangle} from "@fortawesome/pro-regular-svg-icons/faExclamationTriangle";
import { captureFunctions, paymentsEnabled, usesIntents } from 'components/payments/stripe/stripeUtils';
import { filterValues } from 'utils';
import PaymentTestModeWarning from 'components/payments/paymentInputs/PaymentTestModeWarning';

class CreditCardInput extends React.Component {
  static propTypes = {
    ...paymentInputPropTypes,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Amount in dollars
    buttonText: PropTypes.func,
    submittingText: PropTypes.func
  };

  static defaultProps = {
    ...paymentInputDefaultProps,
    amount: 0,
    buttonText: submitButtonText,
    submittingText: (customer, method) => `Submitting ${paymentMethodDisplayName(customer, method)}...`,
    paymentDetails: {}
  };

  constructor(){
    super();

    this.state = {
      submitting: false,
      first_name: null,
      last_name: null,
      error: null,
      cardComplete: false,
      savePaymentProfile: true,
      autopay: true
    };
  }

  handleSubmit(token, {stripe, elements} = {}) {
    const { customer, paymentDetails } = this.props;
    const {
      savePaymentProfile,
      autopay
    } = this.state;

    if (!token) {
      rollbar.error('Attempted to create credit card payment with no token');
      this.handleError({message: this.state.error}); //re-use existing error message if there is one.
      return;
    }

    let extra = {};

    if (usesIntents(customer)) {
      extra = {
        ...captureFunctions({
          paymentDetails,
          confirmSetup: stripe.confirmCardSetup,
          confirmPayment: stripe.confirmCardPayment,
          card: elements.getElement('card'),
        }),
        options: {
          stripe_save_profile: savePaymentProfile
        }
      };
    }

    return this.props.beforeSubmit()
      .then(() => this.props.onSubmit({
        savePaymentProfile,
        token: token.id,
        payment_method: 'credit card',
        paymentDetails,
        autopay,
        ...extra
      }))
      .then(({payment_profile, order}) => this.props.afterSubmit({payment_profile, order}))
      .catch(this.handleError.bind(this))
      .then(() => this.setState({submitting: false}));
  }

  handleError(error) {
    const message = error.message || error.error || "There was an error processing your card.";
    rollbar.error(`Error processing credit card payment: ${message}`, error);

    this.setState({
      error: `Error: ${message}`,
      submitting: false,
    });
  }

  beginSubmit() {
    const { submitting } = this.state;

    if (submitting) {
      throw new Error('duplicate submission');
    } else {
      this.setState({
        error: null,
        submitting: true
      });
    }
  }

  handleCardChange({complete, error}){
    this.setState({
      cardComplete: complete,
      error: null
    });

    if(error){
      this.handleError(error);
    }
  }

  validate(){
    const {
      cardComplete,
      convenienceFeeAccepted
    } = this.state;

    const { paymentDetails } = this.props;

    const convenienceFeeOk = convenienceFeeAccepted || !this.convenienceFeeConfirmationRequired();

    return !!(paymentDetails.valid && cardComplete && convenienceFeeOk);
  }

  convenienceFeeConfirmationRequired(){
    const {
      customer: {
        payment_config: {
          payment_methods: {
            [paymentTypes.CREDIT_CARD]: {
              convenience_fee: {
                show_confirmation_message = false
              } = {}
            } = {}
          } = {}
        } = {}
      },
      convenienceFee
    } = this.props;

    return show_confirmation_message && (convenienceFee != 0);
  }

  render() {
    const {
      billingConfig,
      customer,
      amount,
      paymentDetails,
      showSaveProfileOption,
      showAutopayOption
    } = this.props;

    const buttonText = this.props.buttonText(customer, 'credit card');
    const submittingText = this.props.submittingText(customer, 'credit card');

    const {
      submitting,
      error,
      savePaymentProfile,
      autopay,
      convenienceFeeAccepted
    } = this.state;

    const valid = this.validate();

    const disabled = this.props.disabled || !valid;

    if (!paymentsEnabled(customer.cc_payment_gateway, customer.cc_payment_info)) {
      return <div className={'alert alert-danger'}>
        <FA name={'warning'} nbsp/>
        Credit Card payments have not been configured.  Please contact the system administrator.
      </div>;
    }

    return <StripeForm
      beforeSubmit={this.beginSubmit.bind(this)}
      onSubmit={this.handleSubmit.bind(this)}
      onError={this.handleError.bind(this)}
      details={filterValues(paymentDetails)}
      usesIntents={usesIntents(customer)}
    >
      {error && <div className={'alert alert-danger'}>
        {error}
      </div>}

      <PaymentTestModeWarning testMode={customer.cc_payment_info.test_mode} />

      <fieldset className={'payment-card-input'}>
        <PaymentDetailsInput disabled={submitting} onChange={this.props.paymentDetailsChanged} customer={customer} />

        <label className={`payment-card-field`}>
          <span className={'payment-input-label'}>
            Card:
          </span>

          {global.PAYMENTS_TEST_MODE ?
            <input type={'text'}
                   className={'payment-input-field'}
                   name={'bogus-card-number'}
                   disabled={submitting}
                   onChange={() => this.handleCardChange({complete: true})}
                   placeholder={'The system is in test mode.  This is not a real credit card number field.'}
            />
            :
            <CardElement className={'payment-input-field'}
                         onChange={this.handleCardChange.bind(this)}
                         hidePostalCode={!!paymentDetails.address_zip}
                         disabled={submitting}
                         style={{ base: { lineHeight: '30px' } }}
                         value={{
                           postalCode: paymentDetails.address_zip || undefined
                         }}
            />
          }
        </label>
      </fieldset>

      {this.convenienceFeeConfirmationRequired() && (
        <ConvenienceFeeCheckbox
          customer={customer}
          checked={convenienceFeeAccepted}
          paymentMethod={paymentTypes.CREDIT_CARD}
          onChange={(...params) => {
            this.setState({ convenienceFeeAccepted: !this.state.convenienceFeeAccepted });
          }}
          disabled={submitting}
        />
      )}

      <PaymentProfileOptions
        billingConfig={billingConfig}
        customer={customer}
        showSaveProfileOption={showSaveProfileOption}
        showAutopayOption={showAutopayOption}
        savePaymentProfile={savePaymentProfile}
        paymentMethod={'credit card'}
        autopay={autopay}
        onChange={(props) => this.setState(props)}
        disabled={submitting}
      />

      <div className={'sa-form-submit'}>
        <div className={'sa-form-submit-button'}>
          {submitting ?
            <button className={'btn btn-primary col-24'} disabled>
              <FA name={'spinner'} spin nbsp/>
              {submittingText}
            </button>
            :
            <button className={'btn btn-primary col-24'} disabled={disabled}>{buttonText}</button>
          }
        </div>

        <NachaMessage amount={amount}
                      buttonText={buttonText}
                      customer={customer}/>
      </div>
    </StripeForm>;
  }
}

const mapDucksToProps = () => ({
  billingConfig: singleDuck(billingConfigs, 'current'),
  user: singleDuck(users, 'current'),
  customer: singleDuck(customers, 'current')
});

function mapStateToProps(state, ownProps){
  return {};
}

function mapDispatchToProps(dispatch, ownProps){
  return {}
}

export default duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)(CreditCardInput);
